import { useCallback, useEffect, useState } from "react";
import type { ProductionRun } from "../models";
import type { EntryType } from "../production-entry-label";
import type { OperatorRunActionsWorkspace } from "./useOperatorRunActionsWorkspace";
import type { OperatorDowntimeReasonWorkspace } from "./useOperatorDowntimeReasonWorkspace";

export function useOperatorPanelWorkspace(available: ProductionRun[], selectionScope: string) {
  const [entryType, setEntryType] = useState<EntryType | null>(null);
  const [machineCountOpen, setMachineCountOpen] = useState(false);
  const [materialsOpen, setMaterialsOpen] = useState(false);
  const [scrapSheet, setScrapSheet] = useState<"CHOICE" | "PRODUCT" | "MATERIAL" | null>(null);
  const [casePackagingRunId, setCasePackagingRunId] = useState<string | null>(null);
  const [downtimeConfirmOpen, setDowntimeConfirmOpen] = useState(false);
  const [complaintOpen, setComplaintOpen] = useState(false);
  const [correctionEntry, setCorrectionEntry] = useState<
    ProductionRun["entries"][number] | null
  >(null);
  const [recordsOpen, setRecordsOpen] = useState(false);
  const [handoverOpen, setHandoverOpen] = useState(false);
  const [workOrdersOpen, setWorkOrdersOpen] = useState(false);
  const [completedJobsOpen, setCompletedJobsOpen] = useState(false);
  const [pendingWritesOpen, setPendingWritesOpen] = useState(false);
  const [navigationSelection, setNavigationSelection] = useState<{ scope: string; runId: string | null; }>({ scope: "", runId: null });
  const requestedRunId = navigationSelection.scope === selectionScope ? navigationSelection.runId : null;
  const latestCompletedRunId = [...available].sort((left, right) => Date.parse(right.closedAt ?? "") - Date.parse(left.closedAt ?? ""))[0]?.id ?? null;
  const resolvedRunId = available.find((run) => run.status === "ACTIVE")?.id
    ?? (requestedRunId && available.some((run) => run.id === requestedRunId)
      ? requestedRunId
      : latestCompletedRunId);
  const selected = available.find((run) => run.id === resolvedRunId) ?? null;
  return { entryType, setEntryType, machineCountOpen, setMachineCountOpen, materialsOpen, setMaterialsOpen, scrapSheet, setScrapSheet, casePackagingRunId, setCasePackagingRunId, downtimeConfirmOpen, setDowntimeConfirmOpen, complaintOpen, setComplaintOpen, correctionEntry, setCorrectionEntry, recordsOpen, setRecordsOpen, handoverOpen, setHandoverOpen, workOrdersOpen, setWorkOrdersOpen, completedJobsOpen, setCompletedJobsOpen, pendingWritesOpen, setPendingWritesOpen, navigationSelection, setNavigationSelection, selected };
}

export type OperatorPanelWorkspace = ReturnType<typeof useOperatorPanelWorkspace>;

type NavigationOptions = {
  panels: OperatorPanelWorkspace;
  available: ProductionRun[];
  selectionScope: string;
  selectedId: string | null;
  selectedIndex: number;
  casePackagingOpen: boolean;
  openDowntime: ProductionRun["downtimes"][number] | null;
  reasons: Pick<OperatorDowntimeReasonWorkspace, "visibleDowntimeReason" | "setRequestedDowntimeReason">;
  actions: Pick<OperatorRunActionsWorkspace, "completionOpen" | "setCompletionOpen" | "setCompletionCheck" | "setOperationError" | "setExternalChange" | "setLastSavedAt" | "setSaveFailed">;
};

export function useOperatorPanelNavigation({ panels, available, selectionScope, selectedId, selectedIndex, casePackagingOpen, openDowntime, reasons, actions }: NavigationOptions) {
  const { visibleDowntimeReason, setRequestedDowntimeReason } = reasons;
  const { completionOpen, setCompletionOpen, setCompletionCheck, setOperationError, setExternalChange, setLastSavedAt, setSaveFailed } = actions;
  const { entryType, setEntryType, machineCountOpen, setMachineCountOpen, downtimeConfirmOpen, setDowntimeConfirmOpen, complaintOpen, setComplaintOpen, correctionEntry, setCorrectionEntry, recordsOpen, setRecordsOpen, handoverOpen, setHandoverOpen, workOrdersOpen, setWorkOrdersOpen, completedJobsOpen, setCompletedJobsOpen, pendingWritesOpen, setPendingWritesOpen, setNavigationSelection } = panels;
  const navigateToRun = useCallback((index: number) => {
    const nextRun = available[index];
    if (!nextRun) return;
    setOperationError(null);
    setNavigationSelection({ scope: selectionScope, runId: nextRun.id });
    setEntryType(null);
    setMachineCountOpen(false);
    setDowntimeConfirmOpen(false);
    setComplaintOpen(false);
    setRequestedDowntimeReason(null);
    setCorrectionEntry(null);
    setCompletionOpen(false);
    setCompletionCheck(null);
    setRecordsOpen(false);
    setPendingWritesOpen(false);
    setExternalChange(null);
    setLastSavedAt(null);
    setSaveFailed(false);
  }, [available, selectionScope]);

  useEffect(() => {
    if (!selectedId) return;
    setNavigationSelection((current) =>
      current.scope === selectionScope && current.runId && available.some((run) => run.id === current.runId)
        ? current
        : { scope: selectionScope, runId: selectedId },
    );
  }, [available, selectedId, selectionScope]);

  useEffect(() => {
    if (!openDowntime) return;
    setEntryType(null);
    setMachineCountOpen(false);
    setDowntimeConfirmOpen(false);
    setComplaintOpen(false);
    setCorrectionEntry(null);
    setCompletionOpen(false);
    setCompletionCheck(null);
    setHandoverOpen(false);
    setWorkOrdersOpen(false);
    setCompletedJobsOpen(false);
    setPendingWritesOpen(false);
    setRecordsOpen(false);
  }, [openDowntime?.id]);

  useEffect(() => {
    const navigationSheetOpen = Boolean(casePackagingOpen || entryType || machineCountOpen || downtimeConfirmOpen || complaintOpen || visibleDowntimeReason || correctionEntry || completionOpen || handoverOpen || workOrdersOpen || recordsOpen || completedJobsOpen || pendingWritesOpen);
    if (navigationSheetOpen) return;
    const handleNavigationKey = (event: KeyboardEvent) => {
      if (!event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const target = event.target as HTMLElement | null;
      if (target?.matches("input, textarea, select, [contenteditable='true']")) return;
      if (event.key === "ArrowLeft" && selectedIndex > 0) {
        event.preventDefault();
        navigateToRun(selectedIndex - 1);
      } else if (event.key === "ArrowRight" && selectedIndex >= 0 && selectedIndex < available.length - 1) {
        event.preventDefault();
        navigateToRun(selectedIndex + 1);
      }
    };
    document.addEventListener("keydown", handleNavigationKey);
    return () => document.removeEventListener("keydown", handleNavigationKey);
  }, [available.length, casePackagingOpen, complaintOpen, completionOpen, correctionEntry, downtimeConfirmOpen, entryType, handoverOpen, workOrdersOpen, machineCountOpen, navigateToRun, visibleDowntimeReason, recordsOpen, completedJobsOpen, pendingWritesOpen, selectedIndex]);

  return { navigateToRun };
}
